import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { FiX, FiChevronLeft, FiChevronRight, FiLoader } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import {
  getConsultingCalendarMonth,
  getConsultingCalendarDaySlots,
  createConsultingBookingHold,
} from "../api/mintSpaceConsulting";
import useCheckout from "../hooks/useCheckout";

const WEEK_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function ConsultingBookSlotModal({
  isDark,
  setToast,
  mintSpaceId,
  product,
  onClose,
  onPaid,
}) {
  const { t } = useTranslation();
  const productId = product?._id;
  const [viewMonth, setViewMonth] = useState(() => DateTime.now().toFormat("yyyy-MM"));
  const [selectedDay, setSelectedDay] = useState(() => DateTime.now().toFormat("yyyy-MM-dd"));
  const [daysWithSlots, setDaysWithSlots] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingMonth, setLoadingMonth] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [holding, setHolding] = useState(false);

  const { start: startCheckout, starting: checkoutStarting } = useCheckout({
    onError: (msg) => setToast?.(msg),
    onPaid: () => {
      setToast?.(t("payment.paidAccessGranted") || "Payment complete — your session is booked");
      onPaid?.();
      onClose?.();
    },
  });

  const baseText = "text-label-primary";
  const muted = "text-label-secondary";
  const panel = "border-separator-default bg-bg-raised";

  const loadMonth = useCallback(async () => {
    if (!mintSpaceId || !productId) return;
    setLoadingMonth(true);
    try {
      const res = await getConsultingCalendarMonth(mintSpaceId, productId, viewMonth);
      if (res.success) setDaysWithSlots(res.data.daysWithSlots || []);
    } catch {
      setDaysWithSlots([]);
    } finally {
      setLoadingMonth(false);
    }
  }, [mintSpaceId, productId, viewMonth]);

  const loadSlots = useCallback(async () => {
    if (!mintSpaceId || !productId || !selectedDay) return;
    setLoadingSlots(true);
    try {
      const res = await getConsultingCalendarDaySlots(mintSpaceId, productId, selectedDay);
      if (res.success) setSlots(res.data.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [mintSpaceId, productId, selectedDay]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  useEffect(() => {
    loadSlots();
    setSelectedSlot(null);
  }, [loadSlots]);

  const monthGrid = useMemo(() => {
    const base = DateTime.fromFormat(`${viewMonth}-01`, "yyyy-MM-dd");
    const start = base.startOf("week");
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = start.plus({ days: i });
      const key = d.toFormat("yyyy-MM-dd");
      cells.push({
        key,
        day: d.day,
        inMonth: d.month === base.month,
        hasSlots: daysWithSlots.includes(key),
      });
    }
    return cells;
  }, [viewMonth, daysWithSlots]);

  const confirmAndPay = async () => {
    if (!selectedSlot || !productId) return;
    setHolding(true);
    try {
      const hold = await createConsultingBookingHold(mintSpaceId, {
        productId,
        startAt: selectedSlot.startAt,
      });
      if (!hold.success) throw new Error(hold.message || "Could not reserve slot");
      const bookingId = hold.data?.booking?._id;
      await startCheckout(mintSpaceId, productId, {}, undefined, bookingId);
    } catch (err) {
      setToast?.(err.response?.data?.message || err.message || "Booking failed");
    } finally {
      setHolding(false);
    }
  };

  const busy = holding || checkoutStarting;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center p-3 sm:items-center">
      <button type="button" className="absolute inset-0 bg-black/55" onClick={onClose} aria-label="Close" />
      <div className={`relative z-10 flex max-h-[90vh] w-full max-w-[480px] flex-col overflow-hidden rounded-radius-lg border shadow-overlay ${panel}`}>
        <div className={`flex items-center justify-between border-b px-4 py-3 border-separator-default`}>
          <div>
            <p className={`text-subheadline-semibold ${baseText}`}>
              {t("consulting.pickTimeTitle") || "Pick a time"}
            </p>
            <p className={`text-footnote ${muted}`}>{product?.name}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-radius-xs p-2 hover:bg-black/10">
            <FiX />
          </button>
        </div>

        <div className="overflow-y-auto p-4">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() =>
                setViewMonth(DateTime.fromFormat(`${viewMonth}-01`, "yyyy-MM-dd").minus({ months: 1 }).toFormat("yyyy-MM"))
              }
              className="rounded-radius-xs p-1.5 hover:bg-black/5"
            >
              <FiChevronLeft />
            </button>
            <span className={`text-subheadline-semibold ${baseText}`}>
              {DateTime.fromFormat(`${viewMonth}-01`, "yyyy-MM-dd").toFormat("LLLL yyyy")}
            </span>
            <button
              type="button"
              onClick={() =>
                setViewMonth(DateTime.fromFormat(`${viewMonth}-01`, "yyyy-MM-dd").plus({ months: 1 }).toFormat("yyyy-MM"))
              }
              className="rounded-radius-xs p-1.5 hover:bg-black/5"
            >
              <FiChevronRight />
            </button>
          </div>
          {loadingMonth ? (
            <div className={`flex justify-center py-6 ${muted}`}>
              <FiLoader className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <>
              <div className="mb-1 grid grid-cols-7 gap-1 text-center text-caption-1-semibold opacity-60">
                {WEEK_HEADERS.map((h) => (
                  <span key={h}>{h}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {monthGrid.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    disabled={!c.hasSlots}
                    onClick={() => c.hasSlots && setSelectedDay(c.key)}
                    className={`aspect-square rounded-radius-xs text-footnote font-medium ${
                      selectedDay === c.key
                        ? "bg-app-consulting-deep text-white"
                        : c.hasSlots
                          ? isDark
                            ? "bg-white/10 text-slate-100"
                            : "bg-app-consulting-deep text-app-consulting-soft"
                          : "opacity-30"
                    } ${!c.inMonth ? "opacity-40" : ""}`}
                  >
                    {c.day}
                  </button>
                ))}
              </div>
            </>
          )}

          <p className={`mt-4 text-footnote-semibold ${baseText}`}>
            {t("consulting.availableTimes") || "Available times"}
          </p>
          {loadingSlots ? (
            <div className={`flex py-4 ${muted}`}>
              <FiLoader className="h-4 w-4 animate-spin" />
            </div>
          ) : slots.length === 0 ? (
            <p className={`py-3 text-footnote ${muted}`}>{t("consulting.noSlotsDay") || "No open slots this day."}</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {slots.map((s) => {
                const active = selectedSlot?.startAt === s.startAt;
                const label = DateTime.fromISO(s.startAt).toLocal().toFormat("HH:mm");
                return (
                  <button
                    key={s.startAt}
                    type="button"
                    onClick={() => setSelectedSlot(s)}
                    className={`rounded-radius-xs border px-3 py-1.5 text-footnote-semibold ${
                      active ? "border-app-consulting-base bg-app-consulting-deep text-white" : "border-separator-default"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className={`border-t p-4 border-separator-default`}>
          <button
            type="button"
            disabled={!selectedSlot || busy}
            onClick={confirmAndPay}
            className="w-full rounded-radius-sm bg-accent-default py-2.5 text-subheadline-semibold text-accent-on disabled:opacity-50"
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <FiLoader className="h-4 w-4 animate-spin" />
                {t("consulting.reserving") || "Reserving…"}
              </span>
            ) : (
              t("consulting.continueToPay") || "Continue to payment"
            )}
          </button>
          <p className={`mt-2 text-center text-caption-1 ${muted}`}>
            {t("consulting.holdHint") || "Your slot is held for 15 minutes while you pay."}
          </p>
        </div>
      </div>
    </div>
  );
}
